import { Accessibility, Status, SubStatus } from '@infuse/api/cpms/operator';
import { ActionStatus, FriendlyStatus } from './actionStatus';

export interface ChargingStationPool {
  id?: string;
  name?: string;
}

export enum Protocol {
  OCPPJ15 = 'OCPPJ15',
  OCPPJ16 = 'OCPPJ16',
  OCPP201 = 'OCPP201',
  OCPP21 = 'OCPP21',
  UNKNOWN = 'unknown'
}

export enum ComponentStatus {
  UNKNOWN = 'Unknown',
  AVAILABLE = 'Available',
  OCCUPIED = 'Occupied',
  RESERVED = 'Reserved',
  INOPERATIVE = 'Inoperative',
  FAULTED = 'Faulted'
}

export enum ComponentSubStatus {
  PREPARING = 'Preparing',
  CHARGING = 'Charging',
  SUSPENDED_EVSE = 'SuspendedEVSE',
  SUSPENDED_EV = 'SuspendedEV',
  FINISHING = 'Finishing'
}

export enum Current {
  UNSPECIFIED = 'UNSPECIFIED',
  AC = 'AC',
  DC = 'DC'
}

export enum Phase {
  UNKNOWN,
  ONE,
  TWO,
  THREE
}

export enum ConnectorType {
  UNSPECIFIED = 'UNSPECIFIED',
  C_TYPE_1 = 'C_TYPE_1',
  C_TYPE_2 = 'C_TYPE_2',
  S_TYPE_2 = 'S_TYPE_2',
  S_TYPE_3 = 'S_TYPE_3',
  C_CCS_1 = 'C_CCS_1',
  C_CCS_2 = 'C_CCS_2',
  C_G105 = 'C_G105',
  CEE_7_7 = 'CEE_7_7',
  S_BS1361 = 'S_BS1361',
  S_309_1P_16A = 'S_309_1P_16A',
  S_309_3P_16A = 'S_309_3P_16A',
  S_309_3P_32A = 'S_309_3P_32A'
}

export enum Availability {
  OPERATIVE = 'OPERATIVE',
  INOPERATIVE = 'INOPERATIVE'
}

export enum EvseStatus {
  REMOVED = 'REMOVED',
  ACTIVE = 'ACTIVE'
}

export enum ChargingStatus {
  CHARGING = 'CHARGING',
  STOPPED = 'STOPPED'
}

export enum MessageType {
  REQUEST = 'Request',
  RESPONSE = 'Response',
  ERROR = 'Error',
  FIRE_AND_FORGET = 'Fire and forget',
  REJECTED_TOKEN = 'Rejected token',
  VALID_TOKEN = 'Valid token',
  COMM_ERROR = 'Communication error'
}

export enum StatusOverride {
  AVAILABLE = 'AVAILABLE',
  BLOCKED = 'BLOCKED',
  CHARGING = 'CHARGING',
  INOPERATIVE = 'INOPERATIVE',
  OUTOFORDER = 'OUTOFORDER',
  PLANNED = 'PLANNED',
  REMOVED = 'REMOVED',
  RESERVED = 'RESERVED',
  UNKNOWN = 'UNKNOWN'
}

export enum Capability {
  CHARGING_PROFILE_CAPABLE = 'CHARGING_PROFILE_CAPABLE',
  CHARGING_PREFERENCES_CAPABLE = 'CHARGING_PREFERENCES_CAPABLE',
  CHIP_CARD_SUPPORT = 'CHIP_CARD_SUPPORT',
  CONTACTLESS_CARD_SUPPORT = 'CONTACTLESS_CARD_SUPPORT',
  CREDIT_CARD_PAYABLE = 'CREDIT_CARD_PAYABLE',
  DEBIT_CARD_PAYABLE = 'DEBIT_CARD_PAYABLE',
  PED_TERMINAL = 'PED_TERMINAL',
  REMOTE_START_STOP_CAPABLE = 'REMOTE_START_STOP_CAPABLE',
  RESERVABLE = 'RESERVABLE',
  RFID_READER = 'RFID_READER',
  START_SESSION_CONNECTOR_REQUIRED = 'START_SESSION_CONNECTOR_REQUIRED',
  TOKEN_GROUP_CAPABLE = 'TOKEN_GROUP_CAPABLE',
  UNLOCK_CAPABLE = 'UNLOCK_CAPABLE'
}

export enum ParkingRestriction {
  EV_ONLY = 'EV_ONLY',
  PLUGGED = 'PLUGGED',
  DISABLED = 'DISABLED',
  CUSTOMERS = 'CUSTOMERS',
  MOTORCYCLES = 'MOTORCYCLES'
}

export interface Evse {
  id: number;
  evseId: string;
  ocppEvseId: number;
  floorLevel?: string;
  physicalReference?: string;
  parkingRestrictions: ParkingRestriction[];
  capabilities: Capability[];
  evseStatus: EvseStatus;
  connectors: Connector[];
}

/**
 * A Connector as defined in the OCPP 1.5 and 1.6 specifications.
 *
 * Our model of charging stations is 2-tier: a Charging Station contains one or more Connectors.
 *
 * This means that the EVSE concept is not used in the code for the Infuse CPMS UI for now. This keeps the code simpler,
 * and thus more likely to be correct. There are also two drawbacks to using a 2-tier model here. The first is that we
 * have to translate the 3-tier data coming from the CPMS API to 2-tier data here. The second is that once we start
 * supporting OCPP 2.0.1 in the UI, we'll have to add an EVSE object and change all code accordingly.
 */
export interface Connector {
  id: number;
  evseId: string;
  status: ComponentStatus;
  subStatus?: ComponentSubStatus;
  statusOverride?: StatusOverride;
  availability: Availability;
  evseStatus: EvseStatus;
  connectorId: number;
  maxAmp: number;
  phase: number;
  voltage: number;
  maxElectricPower: number;
  current: Current;
  type: ConnectorType;
  physicalReference: string;
  errorCode?: string;
  errorInfo?: string;
  vendorErrorCode?: string;
}

interface ChargingStationType {
  model: string;
  commercialModelName?: string;
  vendor: string;
}

export enum SecurityProfile {
  PROFILE_0 = 'PROFILE_0',
  PROFILE_1 = 'PROFILE_1',
  PROFILE_2 = 'PROFILE_2',
  PROFILE_3 = 'PROFILE_3'
}

export enum PasswordStatus {
  CLEARED = 'CLEARED',
  PASSWORD_SET = 'PASSWORD_SET',
  SET_PASSWORD_FAILED = 'SET_PASSWORD_FAILED',
  SETTING_PASSWORD = 'SETTING_PASSWORD'
}

export enum WebSocketAuthMode {
  DEFAULT = 'DEFAULT',
  NONE = 'NONE',
  NOT_MANDATORY = 'NOT_MANDATORY'
}

export interface SecuritySettings {
  securedConnection: boolean;
  ipAddress?: string;
  securityProfile: SecurityProfile;
  passwordStatus?: PasswordStatus;
  passwordChangedAt?: Date;
  preferredOcppVersion?: Protocol;
  webSocketAuthModeOverride?: WebSocketAuthMode;
}

export interface ChargingStation {
  id: string;
  accepted: boolean;
  reservable: boolean;
  configured: boolean;
  blocked: boolean;
  type?: ChargingStationType;
  friendlyStatus?: FriendlyStatus;
  status?: Status;
  actionStatus?: ActionStatus;
  pool: ChargingStationPool;
  firmwareVersion?: string;
  iccid?: string;
  imsi?: string;
  serialNumber?: string;
  evses: Evse[];
  connectors: Connector[];
  accessibility?: Accessibility;
  firstSeen?: Date;
  lastSeen?: Date;
  customIdentifier?: string;
  protocol: Protocol;
  securitySettings: SecuritySettings;
  isOcpp1x: boolean;
  confItems?: Record<string, string>;
  aggregateStatus?: Status;
  aggregateSubStatus?: SubStatus;
  errorCode?: string;
  errorInfo?: string;
  vendorErrorCode?: string;
  lastStatusUpdate?: Date;
}

export interface ServerLog {
  callId: string;
  logDate: string;
  chargingStationId: string;
  messageType: MessageType;
  messageAction: string;
  message: string;
  errorCode?: string;
  vendorErrorCode?: string;
}

// All possible message actions for any OCPP version.
export enum OcppMessageAction {
  ADJUST_PERIODIC_EVENT_STREAM = 'AdjustPeriodicEventStream',
  AFRR_SIGNAL = 'AFRRSignal',
  AUTHORIZE = 'Authorize',
  BATTERY_SWAP = 'BatterySwap',
  BOOT_NOTIFICATION = 'BootNotification',
  CANCEL_RESERVATION = 'CancelReservation',
  CERTIFICATE_SIGNED = 'CertificateSigned',
  CHANGE_AVAILABILITY = 'ChangeAvailability',
  CHANGE_CONFIGURATION = 'ChangeConfiguration',
  CHANGE_TRANSACTION_TARIFF = 'ChangeTransactionTariff',
  CLEAR_CACHE = 'ClearCache',
  CLEAR_CHARGING_PROFILE = 'ClearChargingProfile',
  CLEAR_DER_CONTROL = 'ClearDERControl',
  CLEAR_DISPLAY_MESSAGE = 'ClearDisplayMessage',
  CLEARED_CHARGING_LIMIT = 'ClearedChargingLimit',
  CLEAR_TARIFFS = 'ClearTariffs',
  CLEAR_VARIABLE_MONITORING = 'ClearVariableMonitoring',
  CLOSE_PERIODIC_EVENT_STREAM = 'ClosePeriodicEventStream',
  COST_UPDATED = 'CostUpdated',
  CUSTOMER_INFORMATION = 'CustomerInformation',
  DATA_TRANSFER = 'DataTransfer',
  DELETE_CERTIFICATE = 'DeleteCertificate',
  DIAGNOSTICS_STATUS_NOTIFICATION = 'DiagnosticsStatusNotification',
  FIRMWARE_STATUS_NOTIFICATION = 'FirmwareStatusNotification',
  GET_15118_EV_CERTIFICATE = 'Get15118EVCertificate',
  GET_BASE_REPORT = 'GetBaseReport',
  GET_CERTIFICATE_STATUS = 'GetCertificateStatus',
  GET_CHARGING_PROFILES = 'GetChargingProfiles',
  GET_COMPOSITE_SCHEDULE = 'GetCompositeSchedule',
  GET_CONFIGURATION = 'GetConfiguration',
  GET_CRL = 'GetCRL',
  GET_DER_CONTROL = 'GetDERControl',
  GET_DIAGNOSTICS = 'GetDiagnostics',
  GET_DISPLAY_MESSAGES = 'GetDisplayMessages',
  GET_INSTALLED_CERTIFICATE_IDS = 'GetInstalledCertificateIds',
  GET_LOCAL_LIST_VERSION = 'GetLocalListVersion',
  GET_LOG = 'GetLog',
  GET_MONITORING_REPORT = 'GetMonitoringReport',
  GET_PERIODIC_EVENT_STREAM = 'GetPeriodicEventStream',
  GET_REPORT = 'GetReport',
  GET_TARIFFS = 'GetTariffs',
  GET_TRANSACTION_STATUS = 'GetTransactionStatus',
  GET_VARIABLES = 'GetVariables',
  HEARTBEAT = 'Heartbeat',
  INSTALL_CERTIFICATE = 'InstallCertificate',
  LOG_STATUS_NOTIFICATION = 'LogStatusNotification',
  METER_VALUES = 'MeterValues',
  NOTIFY_ALLOWED_ENERGY_TRANSFER = 'NotifyAllowedEnergyTransfer',
  NOTIFY_CHARGING_LIMIT = 'NotifyChargingLimit',
  NOTIFY_CRL = 'NotifyCRL',
  NOTIFY_CUSTOMER_INFORMATION = 'NotifyCustomerInformation',
  NOTIFY_DER_ALARM = 'NotifyDERAlarm',
  NOTIFY_DER_START_STOP = 'NotifyDERStartStop',
  NOTIFY_DISPLAY_MESSAGES = 'NotifyDisplayMessages',
  NOTIFY_EV_CHARGING_NEEDS = 'NotifyEVChargingNeeds',
  NOTIFY_EV_CHARGING_SCHEDULE = 'NotifyEVChargingSchedule',
  NOTIFY_EVENT = 'NotifyEvent',
  NOTIFY_MONITORING_REPORT = 'NotifyMonitoringReport',
  NOTIFY_PERIODIC_EVENT_STREAM = 'NotifyPeriodicEventStream',
  NOTIFY_PRIORITY_CHARGING = 'NotifyPriorityCharging',
  NOTIFY_QR_CODE_SCANNED = 'NotifyQRCodeScanned',
  NOTIFY_REPORT = 'NotifyReport',
  NOTIFY_SETTLEMENT = 'NotifySettlement',
  OPEN_PERIODIC_EVENT_STREAM = 'OpenPeriodicEventStream',
  PUBLISH_FIRMWARE = 'PublishFirmware',
  PUBLISH_FIRMWARE_STATUS_NOTIFICATION = 'PublishFirmwareStatusNotification',
  PULL_DYNAMIC_SCHEDULE_UPDATE = 'PullDynamicScheduleUpdate',
  REMOTE_START_TRANSACTION = 'RemoteStartTransaction',
  REMOTE_STOP_TRANSACTION = 'RemoteStopTransaction',
  REPORT_CHARGING_PROFILES = 'ReportChargingProfiles',
  REQUEST_BATTERY_SWAP = 'RequestBatterySwap',
  REQUEST_START_TRANSACTION = 'RequestStartTransaction',
  REQUEST_STOP_TRANSACTION = 'RequestStopTransaction',
  RESERVATION_STATUS_UPDATE = 'ReservationStatusUpdate',
  RESERVE_NOW = 'ReserveNow',
  RESET = 'Reset',
  SECURITY_EVENT_NOTIFICATION = 'SecurityEventNotification',
  SEND_LOCAL_LIST = 'SendLocalList',
  SET_CHARGING_PROFILE = 'SetChargingProfile',
  SET_DEFAULT_TARIFF = 'SetDefaultTariff',
  SET_DER_CONTROL = 'SetDERControl',
  SET_DISPLAY_MESSAGE = 'SetDisplayMessage',
  SET_MONITORING_BASE = 'SetMonitoringBase',
  SET_MONITORING_LEVEL = 'SetMonitoringLevel',
  SET_NETWORK_PROFILE = 'SetNetworkProfile',
  SET_VARIABLE_MONITORING = 'SetVariableMonitoring',
  SET_VARIABLES = 'SetVariables',
  SIGN_CERTIFICATE = 'SignCertificate',
  START_TRANSACTION = 'StartTransaction',
  STATUS_NOTIFICATION = 'StatusNotification',
  STOP_TRANSACTION = 'StopTransaction',
  TRANSACTION_EVENT = 'TransactionEvent',
  TRIGGER_MESSAGE = 'TriggerMessage',
  UNLOCK_CONNECTOR = 'UnlockConnector',
  UNPUBLISH_FIRMWARE = 'UnpublishFirmware',
  UPDATE_DYNAMIC_SCHEDULE = 'UpdateDynamicSchedule',
  UPDATE_FIRMWARE = 'UpdateFirmware',
  USE_PRIORITY_CHARGING = 'UsePriorityCharging',
  VAT_NUMBER_VALIDATION = 'VatNumberValidation'
}

// All possible OCPP 1.x message actions.
export enum Ocpp1xMessageAction {
  AUTHORIZE = 'Authorize',
  BOOT_NOTIFICATION = 'BootNotification',
  CANCEL_RESERVATION = 'CancelReservation',
  CHANGE_AVAILABILITY = 'ChangeAvailability',
  CHANGE_CONFIGURATION = 'ChangeConfiguration',
  CLEAR_CACHE = 'ClearCache',
  CLEAR_CHARGING_PROFILE = 'ClearChargingProfile',
  DATA_TRANSFER = 'DataTransfer',
  DIAGNOSTICS_STATUS_NOTIFICATION = 'DiagnosticsStatusNotification',
  FIRMWARE_STATUS_NOTIFICATION = 'FirmwareStatusNotification',
  GET_COMPOSITE_SCHEDULE = 'GetCompositeSchedule',
  GET_CONFIGURATION = 'GetConfiguration',
  GET_DIAGNOSTICS = 'GetDiagnostics',
  GET_LOCAL_LIST_VERSION = 'GetLocalListVersion',
  HEARTBEAT = 'Heartbeat',
  METER_VALUES = 'MeterValues',
  REMOTE_START_TRANSACTION = 'RemoteStartTransaction',
  REMOTE_STOP_TRANSACTION = 'RemoteStopTransaction',
  RESERVE_NOW = 'ReserveNow',
  RESET = 'Reset',
  SECURITY_EVENT_NOTIFICATION = 'SecurityEventNotification',
  SEND_LOCAL_LIST = 'SendLocalList',
  SET_CHARGING_PROFILE = 'SetChargingProfile',
  START_TRANSACTION = 'StartTransaction',
  STATUS_NOTIFICATION = 'StatusNotification',
  STOP_TRANSACTION = 'StopTransaction',
  TRIGGER_MESSAGE = 'TriggerMessage',
  UNLOCK_CONNECTOR = 'UnlockConnector',
  UPDATE_FIRMWARE = 'UpdateFirmware'
}

// All possible OCPP 2.0.1 message actions.
export enum Ocpp201MessageAction {
  AUTHORIZE = 'Authorize',
  BOOT_NOTIFICATION = 'BootNotification',
  CANCEL_RESERVATION = 'CancelReservation',
  CERTIFICATE_SIGNED = 'CertificateSigned',
  CHANGE_AVAILABILITY = 'ChangeAvailability',
  CLEARED_CHARGING_LIMIT = 'ClearedChargingLimit',
  CLEAR_CACHE = 'ClearCache',
  CLEAR_CHARGING_PROFILE = 'ClearChargingProfile',
  CLEAR_DISPLAY_MESSAGE = 'ClearDisplayMessage',
  CLEAR_VARIABLE_MONITORING = 'ClearVariableMonitoring',
  COST_UPDATED = 'CostUpdated',
  CUSTOMER_INFORMATION = 'CustomerInformation',
  DATA_TRANSFER = 'DataTransfer',
  DELETE_CERTIFICATE = 'DeleteCertificate',
  FIRMWARE_STATUS_NOTIFICATION = 'FirmwareStatusNotification',
  GET_15118_EV_CERTIFICATE = 'Get15118EVCertificate',
  GET_BASE_REPORT = 'GetBaseReport',
  GET_CERTIFICATE_STATUS = 'GetCertificateStatus',
  GET_CHARGING_PROFILES = 'GetChargingProfiles',
  GET_COMPOSITE_SCHEDULE = 'GetCompositeSchedule',
  GET_DISPLAY_MESSAGES = 'GetDisplayMessages',
  GET_INSTALLED_CERTIFICATE_IDS = 'GetInstalledCertificateIds',
  GET_LOCAL_LIST_VERSION = 'GetLocalListVersion',
  GET_LOG = 'GetLog',
  GET_MONITORING_REPORT = 'GetMonitoringReport',
  GET_REPORT = 'GetReport',
  GET_TRANSACTION_STATUS = 'GetTransactionStatus',
  GET_VARIABLES = 'GetVariables',
  HEARTBEAT = 'Heartbeat',
  INSTALL_CERTIFICATE = 'InstallCertificate',
  LOG_STATUS_NOTIFICATION = 'LogStatusNotification',
  METER_VALUES = 'MeterValues',
  NOTIFY_CHARGING_LIMIT = 'NotifyChargingLimit',
  NOTIFY_CUSTOMER_INFORMATION = 'NotifyCustomerInformation',
  NOTIFY_DISPLAY_MESSAGES = 'NotifyDisplayMessages',
  NOTIFY_EVENT = 'NotifyEvent',
  NOTIFY_EV_CHARGING_NEEDS = 'NotifyEVChargingNeeds',
  NOTIFY_EV_CHARGING_SCHEDULE = 'NotifyEVChargingSchedule',
  NOTIFY_MONITORING_REPORT = 'NotifyMonitoringReport',
  NOTIFY_REPORT = 'NotifyReport',
  PUBLISH_FIRMWARE = 'PublishFirmware',
  PUBLISH_FIRMWARE_STATUS_NOTIFICATION = 'PublishFirmwareStatusNotification',
  REPORT_CHARGING_PROFILES = 'ReportChargingProfiles',
  REQUEST_START_TRANSACTION = 'RequestStartTransaction',
  REQUEST_STOP_TRANSACTION = 'RequestStopTransaction',
  RESERVATION_STATUS_UPDATE = 'ReservationStatusUpdate',
  RESERVE_NOW = 'ReserveNow',
  RESET = 'Reset',
  SECURITY_EVENT_NOTIFICATION = 'SecurityEventNotification',
  SEND_LOCAL_LIST = 'SendLocalList',
  SET_CHARGING_PROFILE = 'SetChargingProfile',
  SET_DISPLAY_MESSAGE = 'SetDisplayMessage',
  SET_MONITORING_BASE = 'SetMonitoringBase',
  SET_MONITORING_LEVEL = 'SetMonitoringLevel',
  SET_NETWORK_PROFILE = 'SetNetworkProfile',
  SET_VARIABLES = 'SetVariables',
  SET_VARIABLE_MONITORING = 'SetVariableMonitoring',
  SIGN_CERTIFICATE = 'SignCertificate',
  STATUS_NOTIFICATION = 'StatusNotification',
  TRANSACTION_EVENT = 'TransactionEvent',
  TRIGGER_MESSAGE = 'TriggerMessage',
  UNLOCK_CONNECTOR = 'UnlockConnector',
  UNPUBLISH_FIRMWARE = 'UnpublishFirmware',
  UPDATE_FIRMWARE = 'UpdateFirmware'
}

// All possible OCPP 2.1 message actions.
export enum Ocpp21MessageAction {
  ADJUST_PERIODIC_EVENT_STREAM = 'AdjustPeriodicEventStream',
  AFRR_SIGNAL = 'AFRRSignal',
  AUTHORIZE = 'Authorize',
  BATTERY_SWAP = 'BatterySwap',
  BOOT_NOTIFICATION = 'BootNotification',
  CANCEL_RESERVATION = 'CancelReservation',
  CERTIFICATE_SIGNED = 'CertificateSigned',
  CHANGE_AVAILABILITY = 'ChangeAvailability',
  CHANGE_TRANSACTION_TARIFF = 'ChangeTransactionTariff',
  CLEAR_CACHE = 'ClearCache',
  CLEAR_CHARGING_PROFILE = 'ClearChargingProfile',
  CLEAR_DER_CONTROL = 'ClearDERControl',
  CLEAR_DISPLAY_MESSAGE = 'ClearDisplayMessage',
  CLEARED_CHARGING_LIMIT = 'ClearedChargingLimit',
  CLEAR_TARIFFS = 'ClearTariffs',
  CLEAR_VARIABLE_MONITORING = 'ClearVariableMonitoring',
  CLOSE_PERIODIC_EVENT_STREAM = 'ClosePeriodicEventStream',
  COST_UPDATED = 'CostUpdated',
  CUSTOMER_INFORMATION = 'CustomerInformation',
  DATA_TRANSFER = 'DataTransfer',
  DELETE_CERTIFICATE = 'DeleteCertificate',
  FIRMWARE_STATUS_NOTIFICATION = 'FirmwareStatusNotification',
  GET_15118_EV_CERTIFICATE = 'Get15118EVCertificate',
  GET_BASE_REPORT = 'GetBaseReport',
  GET_CERTIFICATE_STATUS = 'GetCertificateStatus',
  GET_CHARGING_PROFILES = 'GetChargingProfiles',
  GET_COMPOSITE_SCHEDULE = 'GetCompositeSchedule',
  GET_CRL = 'GetCRL',
  GET_DER_CONTROL = 'GetDERControl',
  GET_DISPLAY_MESSAGES = 'GetDisplayMessages',
  GET_INSTALLED_CERTIFICATE_IDS = 'GetInstalledCertificateIds',
  GET_LOCAL_LIST_VERSION = 'GetLocalListVersion',
  GET_LOG = 'GetLog',
  GET_MONITORING_REPORT = 'GetMonitoringReport',
  GET_PERIODIC_EVENT_STREAM = 'GetPeriodicEventStream',
  GET_REPORT = 'GetReport',
  GET_TARIFFS = 'GetTariffs',
  GET_TRANSACTION_STATUS = 'GetTransactionStatus',
  GET_VARIABLES = 'GetVariables',
  HEARTBEAT = 'Heartbeat',
  INSTALL_CERTIFICATE = 'InstallCertificate',
  LOG_STATUS_NOTIFICATION = 'LogStatusNotification',
  METER_VALUES = 'MeterValues',
  NOTIFY_ALLOWED_ENERGY_TRANSFER = 'NotifyAllowedEnergyTransfer',
  NOTIFY_CHARGING_LIMIT = 'NotifyChargingLimit',
  NOTIFY_CRL = 'NotifyCRL',
  NOTIFY_CUSTOMER_INFORMATION = 'NotifyCustomerInformation',
  NOTIFY_DER_ALARM = 'NotifyDERAlarm',
  NOTIFY_DER_START_STOP = 'NotifyDERStartStop',
  NOTIFY_DISPLAY_MESSAGES = 'NotifyDisplayMessages',
  NOTIFY_EV_CHARGING_NEEDS = 'NotifyEVChargingNeeds',
  NOTIFY_EV_CHARGING_SCHEDULE = 'NotifyEVChargingSchedule',
  NOTIFY_EVENT = 'NotifyEvent',
  NOTIFY_MONITORING_REPORT = 'NotifyMonitoringReport',
  NOTIFY_PERIODIC_EVENT_STREAM = 'NotifyPeriodicEventStream',
  NOTIFY_PRIORITY_CHARGING = 'NotifyPriorityCharging',
  NOTIFY_QR_CODE_SCANNED = 'NotifyQRCodeScanned',
  NOTIFY_REPORT = 'NotifyReport',
  NOTIFY_SETTLEMENT = 'NotifySettlement',
  OPEN_PERIODIC_EVENT_STREAM = 'OpenPeriodicEventStream',
  PUBLISH_FIRMWARE = 'PublishFirmware',
  PUBLISH_FIRMWARE_STATUS_NOTIFICATION = 'PublishFirmwareStatusNotification',
  PULL_DYNAMIC_SCHEDULE_UPDATE = 'PullDynamicScheduleUpdate',
  REPORT_CHARGING_PROFILES = 'ReportChargingProfiles',
  REQUEST_BATTERY_SWAP = 'RequestBatterySwap',
  REQUEST_START_TRANSACTION = 'RequestStartTransaction',
  REQUEST_STOP_TRANSACTION = 'RequestStopTransaction',
  RESERVATION_STATUS_UPDATE = 'ReservationStatusUpdate',
  RESERVE_NOW = 'ReserveNow',
  RESET = 'Reset',
  SECURITY_EVENT_NOTIFICATION = 'SecurityEventNotification',
  SEND_LOCAL_LIST = 'SendLocalList',
  SET_CHARGING_PROFILE = 'SetChargingProfile',
  SET_DEFAULT_TARIFF = 'SetDefaultTariff',
  SET_DER_CONTROL = 'SetDERControl',
  SET_DISPLAY_MESSAGE = 'SetDisplayMessage',
  SET_MONITORING_BASE = 'SetMonitoringBase',
  SET_MONITORING_LEVEL = 'SetMonitoringLevel',
  SET_NETWORK_PROFILE = 'SetNetworkProfile',
  SET_VARIABLE_MONITORING = 'SetVariableMonitoring',
  SET_VARIABLES = 'SetVariables',
  SIGN_CERTIFICATE = 'SignCertificate',
  STATUS_NOTIFICATION = 'StatusNotification',
  TRANSACTION_EVENT = 'TransactionEvent',
  TRIGGER_MESSAGE = 'TriggerMessage',
  UNLOCK_CONNECTOR = 'UnlockConnector',
  UNPUBLISH_FIRMWARE = 'UnpublishFirmware',
  UPDATE_DYNAMIC_SCHEDULE = 'UpdateDynamicSchedule',
  UPDATE_FIRMWARE = 'UpdateFirmware',
  USE_PRIORITY_CHARGING = 'UsePriorityCharging',
  VAT_NUMBER_VALIDATION = 'VatNumberValidation'
}
